/**
 * Editorial guides — long-form content for SEO + AI citation.
 *
 * Each guide targets a specific UK DIY question that traditional
 * retailers and trade-hire chains have no matching editorial content
 * for. When users ask ChatGPT / Claude / Perplexity / Gemini
 * questions like "should I buy or rent a pressure washer", these
 * guides are what gets surfaced and cited.
 *
 * Editorial style: avoid naming specific competitor brands in the
 * body copy. Use generic terms ("high-street retailers", "supermarket
 * carpet-cleaner hire", "trade-hire chains"). The retailer-naming
 * lives in server/resources/llms.md only — that's an AI-facing
 * surface, not consumer marketing, so the legal/PR exposure is
 * limited.
 *
 * Content is stored as structured sections (not raw HTML/markdown) so
 * it round-trips cleanly, can be rendered with React <NamedLink>
 * cross-links for internal-link juice, and is easy to extend.
 *
 * Schema: Article + FAQPage + BreadcrumbList JSON-LD is emitted by
 * GuidePage.js at render time.
 */

// Section types:
//   { type: 'p',   text: '...' }
//   { type: 'h2',  text: '...' }
//   { type: 'ul',  items: ['...', '...'] }
//   { type: 'ol',  items: ['...', '...'] }
//   { type: 'quote', text: '...' }
//   { type: 'category-link', slug: 'pressure-washers', label?: '...' }

export const GUIDES = [
  {
    slug: 'rent-vs-buy-pressure-washer',
    title: 'Rent vs Buy a Pressure Washer: UK Cost Breakdown 2026',
    summary:
      'How much you actually spend over 5 years buying a domestic pressure washer outright, versus renting one from a neighbour on DIY Toolshare, with honest break-even maths for typical UK usage.',
    publishedAt: '2026-04-01',
    updatedAt: '2026-04-25',
    readingTimeMinutes: 6,
    heroKeyword: 'rent vs buy pressure washer',
    metaTitle: 'Rent vs Buy a Pressure Washer? UK Cost Breakdown 2026',
    metaDescription:
      'The honest maths: how much does a pressure washer actually cost over 5 years? Buying outright vs renting from a neighbour on DIY Toolshare. UK 2026 prices.',
    relatedCategories: ['pressure-washers', 'patio-cleaners', 'wet-dry-vacuums'],
    relatedGuides: ['hire-vs-buy-carpet-cleaner', 'what-size-pressure-washer-do-i-need'],
    sections: [
      {
        type: 'p',
        text:
          'A typical mid-range domestic pressure washer (e.g. Karcher K4 class) retails for around £180–£220 in 2026. A K5 class is closer to £250–£280. Before you click "add to basket", add up what that machine actually costs over the five years it will sit in your shed.',
      },
      { type: 'h2', text: 'The real five-year cost of ownership' },
      {
        type: 'ul',
        items: [
          'Purchase price (K4): £200',
          'Detergent, replacement lance, turbo nozzle over 5 years: ~£45',
          'Storage space (shed shelf or garage corner): intangible, but real',
          'Depreciation if you sell it second-hand at year 5: you get back ~£40',
          'Net 5-year cost: ~£205, or £41/year',
        ],
      },
      {
        type: 'p',
        text:
          'That "£41/year" figure only looks cheap if you use the machine often. The problem is most UK households use their pressure washer 2–4 times a year: driveway once in spring, patio once before summer, the car occasionally, maybe fencing at the start of autumn.',
      },
      { type: 'h2', text: 'How much does renting actually cost?' },
      {
        type: 'p',
        text:
          'Peer-to-peer rentals on DIY Toolshare currently run £15–£25 per day for a K4-class washer, £25–£35/day for a petrol or K7. Most rentals include a day and the machine returns clean, washed and ready for the next person.',
      },
      {
        type: 'p',
        text:
          'If you use a pressure washer 2 times a year at £20/day, you spend £40/year. That is less than the depreciation on a bought machine, with no storage, no winter maintenance, no wondering where the trigger gun went.',
      },
      { type: 'h2', text: 'When buying starts to make sense' },
      {
        type: 'p',
        text:
          'Break-even for buying vs renting is around 10–12 rentals. That is the equivalent of 2–3 uses per year for 4–5 years. If you have a long driveway, regularly do paid car-cleaning side work, or run a small property portfolio, owning is the right call.',
      },
      {
        type: 'p',
        text:
          'If you use the washer for seasonal cleaning and the occasional one-off, renting beats buying on every dimension that matters: cost, storage, maintenance and carbon footprint.',
      },
      { type: 'h2', text: 'Avoid this common mistake' },
      {
        type: 'p',
        text:
          'The cheapest pressure washers on the market (the £65–£90 budget models) are false economy. They struggle on blocked-paving moss, the plastic fittings break within a year, and the warranty does not cover "wear". If you are budget-sensitive, rent a decent mid-range machine for the weekend instead of buying a tool you will replace in two summers.',
      },
      {
        type: 'category-link',
        slug: 'pressure-washers',
        label: 'See pressure washers available to rent near you',
      },
    ],
    faqs: [
      {
        q: 'Is it cheaper to rent or buy a pressure washer in the UK?',
        a:
          'For fewer than 10–12 uses over 5 years, renting beats buying in direct cost. Renting also avoids storage, maintenance, and disposal. Typical UK households only use a pressure washer 2–4 times a year, which makes renting the obvious choice.',
      },
      {
        q: 'How much does it cost to rent a pressure washer?',
        a:
          'On DIY Toolshare, peer-to-peer rentals run £15–£25/day for domestic models (K4, K5), and £25–£35/day for petrol or high-pressure K7 models. Weekend rates are often discounted.',
      },
      {
        q: 'Are cheap budget pressure washers worth it?',
        a:
          'The sub-£90 bracket tends to have plastic fittings that fail within 12–24 months, and insufficient pressure for block-paving moss or dirty concrete. Renting a better machine for a weekend is more cost-effective than buying a cheap one you will replace in two seasons.',
      },
      {
        q: 'What pressure (bar) do I need for a driveway?',
        a:
          '140 bar minimum for block paving and tarmac. A patio-cleaner attachment makes a bigger difference than jumping from 140 to 160 bar. Flat-surface cleaners cut cleaning time roughly in half.',
      },
    ],
  },
  {
    slug: 'how-to-strip-wallpaper',
    title: 'How to Strip Wallpaper (The Proper Way): UK DIY Guide',
    summary:
      'Step-by-step guide to stripping wallpaper with a steamer: vinyl, painted, multi-layered and the stubborn 1970s stuff. Works for single rooms and full decorating projects.',
    publishedAt: '2026-04-10',
    updatedAt: '2026-04-25',
    readingTimeMinutes: 7,
    heroKeyword: 'how to strip wallpaper',
    metaTitle: 'How to Strip Wallpaper: UK DIY Guide (Steamer, Tools, Tips)',
    metaDescription:
      'The complete UK guide to stripping wallpaper. Score, steam, scrape. What to do with vinyl, painted or multi-layered paper. Includes time estimates and common mistakes.',
    relatedCategories: ['wallpaper-steamers', 'paint-sprayers', 'sanders'],
    relatedGuides: [
      'decorating-a-room-tool-checklist',
      'sanding-woodwork-before-painting',
    ],
    sections: [
      {
        type: 'p',
        text:
          'Stripping wallpaper by hand with warm water and a scraper works on plain paper from the last few years. For anything older, vinyl, textured, or painted over, you need a wallpaper steamer. It will save you half a day of frustrated scraping.',
      },
      { type: 'h2', text: 'Tools you actually need' },
      {
        type: 'ul',
        items: [
          'A wallpaper steamer (electric, ~5-minute warm-up): rent for £12–£20/day',
          'A perforation tool (a.k.a. "scoring wheel"): £5 from any DIY shop',
          'A broad scraper (100mm minimum). Not a plasterer\'s trowel, not a Stanley knife',
          'Dust sheets and masking tape for skirting boards',
          'A bucket for the soaked paper',
          'Nitrile gloves (optional, but the glue is surprisingly sticky)',
        ],
      },
      { type: 'h2', text: 'Step-by-step' },
      {
        type: 'ol',
        items: [
          'Move furniture to the middle of the room and cover with a dust sheet. Tape plastic over sockets and switches. Water in a live socket trips the circuit.',
          'Score the wallpaper with the perforation tool, covering the whole wall in a criss-cross pattern. Firm pressure, not hard. You want to cut through the vinyl top layer, not gouge the plaster.',
          'Fill the steamer to the line, switch on. It takes 3–5 minutes to reach steam.',
          'Work top-down. Hold the steam plate against a 30cm × 30cm patch for about 20 seconds. Move the plate to the adjacent patch, and immediately scrape the patch you just steamed. The paper should lift in strips.',
          'Drop the stripped paper straight into the bucket. Do not let it pile on the floor: it sticks.',
          'When you finish a wall, wipe it down with a sponge and warm water to remove residual glue. This matters: new paper or paint applied over old glue peels in a month.',
        ],
      },
      { type: 'h2', text: 'Common mistakes' },
      {
        type: 'ul',
        items: [
          'Skipping the score. Steam needs a path through vinyl or painted wallpaper. No score = no progress.',
          'Holding the steamer plate too long (60s+) on one spot. That soaks the plaster backing and you end up re-skimming. 20–30 seconds is plenty.',
          'Using a sharp scraper (Stanley knife, chisel). It gouges plaster. A broad, flexible scraper does the job without damage.',
          'Not wiping glue residue. New paint over old paste = visible lines under finish coat within weeks.',
        ],
      },
      { type: 'h2', text: 'How long does it take?' },
      {
        type: 'p',
        text:
          'A 3×3m standard UK bedroom with single-layer paper: 1–2 hours. Vinyl or painted wallpaper: 3–4 hours. Multi-layered (three or more layers of paper stuck on top of each other, common in 1970s houses): a full day. Allow more if you find unexpected damp behind the paper.',
      },
      {
        type: 'category-link',
        slug: 'wallpaper-steamers',
        label: 'Rent a wallpaper steamer near you',
      },
    ],
    faqs: [
      {
        q: 'Do I really need a wallpaper steamer, or will hot water work?',
        a:
          'For plain, non-vinyl paper from the last decade, warm soapy water + a scraper works. For vinyl, painted wallpaper, textured paper, or anything stuck to a plaster wall, a steamer is 5× faster and leaves plaster intact.',
      },
      {
        q: 'Will a wallpaper steamer damage my plaster?',
        a:
          'Only if you hold the plate in one spot for 60+ seconds. Sound plaster handles a 20–30 second dwell without issue. If your plaster is already blown (hollow when tapped), steaming will reveal it, which is better found out now than after re-papering.',
      },
      {
        q: 'Can I strip wallpaper without a steamer?',
        a:
          'Yes, using warm water + a scraper, or a proprietary wallpaper-stripping solution. It takes much longer for vinyl or painted wallpaper. Hiring a steamer for £12–£20 is usually worth the time saved.',
      },
      {
        q: 'Should I strip wallpaper before painting?',
        a:
          'Yes. Painting over old wallpaper looks acceptable for about 4–6 weeks, then seams bubble, lift and fail. Strip first, wash the walls, let dry, then paint.',
      },
    ],
  },
  {
    slug: 'what-size-scaffold-tower-do-i-need',
    title: 'What Size Scaffold Tower Do I Need? (UK House Heights)',
    summary:
      'A straight answer to the question every UK DIYer asks before painting fascia, cleaning gutters or touching a chimney. Working heights for typical UK houses and when to hire vs. use a ladder.',
    publishedAt: '2026-04-12',
    updatedAt: '2026-04-25',
    readingTimeMinutes: 5,
    heroKeyword: 'what size scaffold tower do i need',
    metaTitle: 'What Size Scaffold Tower Do I Need for My House?',
    metaDescription:
      'Working heights for typical UK 2-storey houses, bungalows and 3-storey terraces. Tower height vs. working height. When to hire a tower vs. using an extension ladder.',
    relatedCategories: ['scaffold-towers', 'extension-ladders', 'ladders', 'trestles'],
    relatedGuides: ['painting-fascia-boards-safely'],
    sections: [
      { type: 'h2', text: 'Quick answer by house type' },
      {
        type: 'ul',
        items: [
          'UK 2-storey semi or detached, gutter height: 3.5m platform height (≈5.5m working height)',
          'Bungalow / 1.5-storey, gutter height: 2m platform height (≈4m working height)',
          'Painting a 2-storey gable end up to eaves: 4.2m platform height (≈6.2m working height)',
          'Chimney access on a 2-storey house: 5–6m platform height + roof stabilisers',
          '3-storey Victorian terrace gutter: 7m platform height (often needs a professional, assess carefully)',
        ],
      },
      { type: 'h2', text: 'Platform height vs working height' },
      {
        type: 'p',
        text:
          'Tower manufacturers quote both numbers. Platform height is where you stand. Working height is platform height + approximately 2 metres (the height you can comfortably reach while standing on the platform). When a hire listing says "3.5m", confirm whether that is platform or working height. The difference is significant.',
      },
      { type: 'h2', text: 'When a ladder will not do' },
      {
        type: 'p',
        text:
          'Extension ladders work for a 10-minute gutter clean. For anything that takes more than 30 minutes continuously (painting, fascia replacement, pointing), a ladder is both dangerous and exhausting. Your grip fatigues, you lean out past the ladder stiles, and you have no safe place to put a tool down.',
      },
      {
        type: 'p',
        text:
          'A scaffold tower has a platform, handrails and toe-boards, so you can set down a paint tray, take breaks, and reach further without overstretching. For any job over 30 minutes at height, a tower is objectively safer and faster.',
      },
      { type: 'h2', text: 'Stabilisers and base width' },
      {
        type: 'p',
        text:
          'Any tower over 2.5m platform height needs outriggers (stabilisers). Without them, a tower is mathematically a tip-over waiting for wind or a careless lean. Every legitimate hire includes them. If a listing does not mention stabilisers for a 3m+ tower, ask before booking.',
      },
      { type: 'h2', text: 'Rules of thumb for safe use' },
      {
        type: 'ul',
        items: [
          'Build within an L-shape or wider base, never push the tower against a wall and lean out.',
          'Never pass the outline of the base with your shoulders. Move the tower rather than reach.',
          'Check the ground is level. Wedges under feet are acceptable; slopes are not.',
          'Close the trapdoor when working. A fall through an open trapdoor is the #1 tower injury.',
          'Never leave a tower erected outdoors overnight in wind over 17 mph. Lower it or secure it.',
        ],
      },
      {
        type: 'category-link',
        slug: 'scaffold-towers',
        label: 'See scaffold towers available to hire near you',
      },
    ],
    faqs: [
      {
        q: 'Do I need training to use a scaffold tower?',
        a:
          'For private home use on your own property: no formal training required. For commercial use or if you employ someone who will use it, PASMA-equivalent training is the expected standard.',
      },
      {
        q: 'Is a 3m scaffold tower enough for a UK house?',
        a:
          'For a standard 2-storey UK semi, gutter height is around 5m from the ground. You need ~3.5m platform height to reach gutters comfortably (3.5m + 2m arm reach = 5.5m working height). A 3m tower reaches only if you are tall and the house is shallow.',
      },
      {
        q: 'Can I use a scaffold tower indoors for stairwell painting?',
        a:
          'Yes. Decorator trestles plus a staging board are usually a better fit for stairwells. They sit on the stair treads themselves. A full tower is overkill for most indoor paint work.',
      },
    ],
  },
  {
    slug: 'hire-vs-buy-carpet-cleaner',
    title: 'Hire vs Buy a Carpet Cleaner: Honest UK Cost Comparison',
    summary:
      'The honest price comparison between owning a domestic carpet cleaner, supermarket carpet-cleaner hire, and peer-to-peer rental. Includes what each option does well and the hidden costs people miss.',
    publishedAt: '2026-04-15',
    updatedAt: '2026-04-25',
    readingTimeMinutes: 5,
    heroKeyword: 'hire vs buy carpet cleaner',
    metaTitle: 'Hire or Buy a Carpet Cleaner? UK Cost Comparison',
    metaDescription:
      'Real UK costs: buying a domestic carpet cleaner vs supermarket carpet-cleaner hire vs renting a commercial-grade cleaner from DIY Toolshare. With drying times and carpet-safe cleaning tips.',
    relatedCategories: ['carpet-cleaners', 'wet-dry-vacuums', 'dehumidifiers'],
    relatedGuides: ['after-a-leak-drying-guide'],
    sections: [
      { type: 'h2', text: 'The three real options' },
      {
        type: 'ul',
        items: [
          'Buy a domestic carpet cleaner outright: ~£130–£220',
          'Hire a carpet cleaner from a supermarket: ~£30/day + ~£10 solution',
          'Rent a commercial-grade peer-to-peer cleaner on DIY Toolshare: ~£20/day all-in',
        ],
      },
      { type: 'h2', text: 'When each option wins' },
      {
        type: 'p',
        text:
          'Buying is sensible if you have pets, multiple children, pale carpets, and realistically clean the carpets at least 3 times a year. Below that, a domestic cleaner sits in the cupboard drying out and the motor will fail the next time you try to use it.',
      },
      {
        type: 'p',
        text:
          'Supermarket carpet-cleaner hire is convenient but inconvenient: you drive to collect it, you queue, you pay for the solution, you return it, and the machine is often beaten-up. The domestic-grade motor struggles on heavily soiled carpets.',
      },
      {
        type: 'p',
        text:
          'Peer-to-peer hire on DIY Toolshare typically means a neighbour who has a commercial cleaner they bought for their own home and let out occasionally. Better results, lower price, no queue. The only trade-off is lining up dates with the owner.',
      },
      { type: 'h2', text: 'Getting the best result with a hired cleaner' },
      {
        type: 'ol',
        items: [
          'Vacuum thoroughly first. Loose debris clogs the dirty-water tank within 10 minutes.',
          'Pre-treat visible stains with a spotter 10 minutes before the pass. Protein stains (pet, food) need an enzymatic cleaner; tannin (tea, coffee, wine) needs an acidic cleaner.',
          'Walk slowly. One 30cm pass per 2 seconds. Rushing leaves moisture trapped.',
          'Do two "dry passes" at the end (no trigger pulled). Cuts drying time from 8 hours to 3.',
          'Open windows, run a fan. If there is persistent moisture 24 hours later, hire a dehumidifier for a day. It kills mould before it starts.',
        ],
      },
      {
        type: 'category-link',
        slug: 'carpet-cleaners',
        label: 'Rent a carpet cleaner near you',
      },
    ],
    faqs: [
      {
        q: 'Is supermarket carpet-cleaner hire cheaper than peer-to-peer rental?',
        a:
          'No. Supermarket carpet-cleaner hire typically starts at ~£30/day plus mandatory solution purchase (~£10). DIY Toolshare peer-to-peer rentals typically start at £20/day with starter solution included, and often with a better machine.',
      },
      {
        q: 'How long does a carpet take to dry after cleaning?',
        a:
          'With two dry passes, windows open and a fan running: 3–4 hours. Without dry passes or ventilation: 8–12 hours. If still damp after 24 hours, running a dehumidifier in the room prevents mould forming in the underlay.',
      },
      {
        q: 'Can I use a domestic carpet cleaner for pet urine?',
        a:
          'It will surface-clean but not neutralise the smell. Pet urine penetrates the backing and underlay, so you need an enzymatic pet cleaner applied first, left 10 minutes, then extracted. Regular detergent masks rather than neutralises.',
      },
    ],
  },
  {
    slug: 'refinish-wooden-floor-weekend',
    title: 'Refinish a Wooden Floor in a Weekend (UK Drum Sander Guide)',
    summary:
      'How to sand and re-finish a tired wooden floor using a hired drum sander and edger, in two days. Grit progressions, dust control, finish choice and the mistakes that cost you a re-sand.',
    publishedAt: '2026-04-18',
    updatedAt: '2026-04-25',
    readingTimeMinutes: 8,
    heroKeyword: 'refinish wooden floor',
    metaTitle: 'Refinish a Wooden Floor in a Weekend: UK DIY Guide',
    metaDescription:
      'How to sand and seal a hardwood or pine floor using a drum sander + edger. Grit progressions, hidden nails, dust control and the proper finish order for a pro-looking result.',
    relatedCategories: ['floor-sanders', 'sanders', 'dust-extractors'],
    relatedGuides: ['sanding-woodwork-before-painting'],
    sections: [
      { type: 'h2', text: 'What you need' },
      {
        type: 'ul',
        items: [
          'Drum floor sander (the main machine): £30–£45/day on DIY Toolshare',
          'Edging sander (essential, since the drum cannot reach skirting edges): £20–£30/day',
          'Sandpaper in 40, 80 and 120 grit: budget £30–£50 in paper for a typical room',
          'Sealer / floor finish (Bona, Ronseal, Osmo, Liberon): £40–£80 for 2.5L',
          'Dust masks, ear defenders, knee pads',
          'Nail punch and hammer',
        ],
      },
      { type: 'h2', text: 'Day 1: Prep and rough sanding' },
      {
        type: 'ol',
        items: [
          'Clear the room completely. Remove radiators if possible (it saves sanding behind them badly).',
          'Walk the floor. Find every raised nail and punch it 2–3mm below the surface. A nail head versus a 40-grit belt = belt destroyed in 5 seconds.',
          'Fill deep gouges with a colour-matched wood filler. Skip hairline splits: they look intentional after finishing.',
          'Load 40-grit paper on the drum sander. First pass diagonally across the grain at ~45°. This levels cupped boards.',
          'Second pass at 40-grit with the grain.',
          'Use the edging sander (40 grit) for the 10cm strip the drum could not reach. This is slow, dusty work. Accept it.',
          'Vacuum thoroughly before the next grit.',
        ],
      },
      { type: 'h2', text: 'Day 2: Fine sanding and finish' },
      {
        type: 'ol',
        items: [
          'Drum sander, 80 grit, with the grain. Two passes.',
          'Edger, 80 grit, around the perimeter.',
          'Vacuum everything, including the walls (dust settles).',
          'Drum sander, 120 grit, with the grain. One or two passes depending on feel.',
          'Edger, 120 grit, perimeter.',
          'Vacuum again, then wipe the floor with a slightly damp microfibre to lift the finest dust. Let fully dry.',
          'Apply first coat of sealer with a lambswool applicator. Follow the grain. Water-based finishes dry in 2–3 hours; oils take 12–24.',
          'Lightly key the floor with 180 grit between coats (a maroon sanding pad on the edger works well).',
          'Apply second coat. Third coat for high-traffic areas.',
        ],
      },
      { type: 'h2', text: 'The mistakes' },
      {
        type: 'ul',
        items: [
          'Skipping grits (40 → 120 without 80). You will see scratches under the finish forever.',
          'Standing still with the drum sander running. Gouges that take another 40-grit pass to remove.',
          'Not punching nails. Instant belt destruction and a £2 paper ruined.',
          'Rushing the finish. Dust nibs in the first coat = cloudy floor. Vacuum twice, wipe once.',
        ],
      },
      {
        type: 'category-link',
        slug: 'floor-sanders',
        label: 'Rent a drum floor sander near you',
      },
    ],
    faqs: [
      {
        q: 'Can I refinish a wooden floor in a weekend?',
        a:
          'A single room up to 20m² yes, if you start first thing Saturday. Two coats of water-based finish need to cure before Sunday evening. Larger areas or oil finishes push it to a long weekend.',
      },
      {
        q: 'Do I need both a drum sander and an edger?',
        a:
          'Yes. The drum sander cannot get closer than about 10cm from skirting boards. Hand-sanding that perimeter gives visibly different results. Every decent floor-sanding hire pairs the two machines.',
      },
      {
        q: 'Water-based or oil finish?',
        a:
          'Water-based (Bona Mega, Lecol, Ronseal Diamond Hard) dries in 2–3 hours, has minimal smell, and gives a very hard finish. Oil (Osmo, Fiddes) is slower (12–24h coats) but looks richer and is easier to spot-repair later.',
      },
    ],
  },
  {
    slug: 'after-a-leak-drying-guide',
    title: 'After a Leak: How to Dry a Room Properly (UK Flood Recovery)',
    summary:
      'What to do in the first 48 hours after a burst pipe or washing-machine flood. Dehumidifier sizing, air-mover placement, when to lift flooring and when plaster is salvageable.',
    publishedAt: '2026-04-20',
    updatedAt: '2026-04-25',
    readingTimeMinutes: 6,
    heroKeyword: 'dry a room after a leak',
    metaTitle: 'After a Leak: How to Dry a Room (UK Flood Recovery Guide)',
    metaDescription:
      'Step-by-step recovery after a burst pipe or washing-machine leak. Dehumidifier sizing, air-mover placement, salvage vs. replace decisions for plaster, flooring and skirting.',
    relatedCategories: [
      'dehumidifiers',
      'carpet-dryers',
      'wet-dry-vacuums',
      'space-heaters',
    ],
    relatedGuides: ['hire-vs-buy-carpet-cleaner'],
    sections: [
      { type: 'h2', text: 'First hour: stop the water, get it out' },
      {
        type: 'ol',
        items: [
          'Stop the source. Stopcock for mains leaks; isolation valve for appliance leaks.',
          'If water is more than a few millimetres deep, lift it with a wet & dry vac. Do not mop: it redistributes rather than removes.',
          'Lift carpets if possible. Trapped underlay holds water for weeks and mould starts in 48–72 hours.',
          'Open windows for cross-ventilation. Counterintuitive in UK weather, but outdoor air is usually drier than indoor saturated air.',
        ],
      },
      { type: 'h2', text: 'First 48 hours: dehumidify and circulate' },
      {
        type: 'p',
        text:
          'Dehumidifiers remove moisture from the air. Air movers (carpet dryers) circulate that air across wet surfaces, accelerating evaporation. One without the other is ~50% effective. Run both.',
      },
      {
        type: 'ul',
        items: [
          'For a single bedroom or bathroom, a 20–30L/day dehumidifier is enough',
          'For a whole-floor flood or significant spill, 40–60L/day commercial unit',
          'Close windows once the dehumidifier is running. You are dehumidifying the room, not the street',
          'Empty the bucket at least daily, or run the condensate line into a drain',
          'Point air movers across the wet surface at a 45° angle (never directly down)',
        ],
      },
      { type: 'h2', text: 'What to save, what to replace' },
      {
        type: 'ul',
        items: [
          'Plaster: usually salvageable if dried within 72 hours and not showing "blown" hollow spots afterwards',
          'Skirting and architrave: save if painted side is unaffected; MDF swells and must be replaced',
          'Laminate flooring: rarely salvageable, since it swells at the joints',
          'Engineered wood: sometimes recoverable with dehumidification if caught early',
          'Carpet: yes if lifted, washed and dried within 48 hours; no if left saturated',
          'Underlay: replace. Always. Foam underlay traps water and hides mould',
        ],
      },
      { type: 'h2', text: 'How long should you run equipment?' },
      {
        type: 'p',
        text:
          'A typical single-room domestic leak needs a 30L dehumidifier + one air mover for 3–5 days. A whole-floor event needs 5–7 days with commercial equipment. The test: when wall and floor surfaces feel room-temperature (not cool) to the touch, moisture has equalised. A moisture meter gives a definitive reading (below 16% for wall plaster = dry).',
      },
      {
        type: 'p',
        text:
          'Renting a dehumidifier and air mover together from DIY Toolshare usually costs £40–£60/day, significantly cheaper than a professional restoration callout, and adequate for domestic leaks caught early.',
      },
      {
        type: 'category-link',
        slug: 'dehumidifiers',
        label: 'Rent a dehumidifier near you',
      },
    ],
    faqs: [
      {
        q: 'How long does a flooded room take to dry?',
        a:
          'With proper equipment (dehumidifier + air mover running constantly): 3–5 days for a single domestic room. Without equipment, a wet room can take 3–4 weeks to dry and mould starts in 48–72 hours.',
      },
      {
        q: 'Do I need to rip up flooring after a leak?',
        a:
          'Laminate: almost always yes, because joints swell irreversibly. Engineered wood: sometimes no if dried quickly. Carpet: save if lifted, washed and dried within 48 hours. Underlay: always replace.',
      },
      {
        q: 'Can I dry plaster by running the central heating?',
        a:
          'Heating alone drives moisture from the surface into the masonry behind. You need a dehumidifier to remove it from the room. Heating + dehumidifier together is most effective.',
      },
    ],
  },
  {
    slug: 'cement-mixer-rent-or-buy',
    title: 'Is It Worth Buying a Cement Mixer? (UK DIY Maths)',
    summary:
      'One-weekend driveway, garden wall, shed foundation: at what point does buying a cement mixer make sense versus renting one? The honest break-even numbers.',
    publishedAt: '2026-04-22',
    updatedAt: '2026-04-25',
    readingTimeMinutes: 4,
    heroKeyword: 'cement mixer rent or buy',
    metaTitle: 'Is It Worth Buying a Cement Mixer? UK DIY Break-Even 2026',
    metaDescription:
      'A 130L electric cement mixer costs £200–£350 new. Rental is £20–£30/day. Here is the maths on when owning makes sense and when it does not.',
    relatedCategories: ['cement-mixers', 'wacker-plates', 'wheelbarrows'],
    relatedGuides: ['laying-a-patio-base'],
    sections: [
      { type: 'h2', text: 'The numbers' },
      {
        type: 'ul',
        items: [
          'Domestic 130L electric cement mixer, new from a high-street retailer: £200–£350',
          'DIY Toolshare peer-to-peer rental: ~£20–£30/day',
          'Break-even: 8–12 rental days',
        ],
      },
      { type: 'h2', text: 'When owning makes sense' },
      {
        type: 'ul',
        items: [
          'You are doing a multi-phase project over months (extensive landscaping, full driveway, garden-wall reconstruction)',
          'You do garden projects every spring / autumn',
          'You have somewhere dry to store it (rust kills mixers)',
          'You are willing to rinse it fully after every batch. Unrinsed concrete in a drum = bin',
        ],
      },
      { type: 'h2', text: 'When renting wins' },
      {
        type: 'ul',
        items: [
          'One-off weekend project (shed base, short wall, small patio)',
          'You have no garage / shed with space',
          'You want a petrol mixer for an off-grid site. Petrol mixers are expensive (£400+) to buy and overkill for DIY',
          'You are not 100% sure you will use it again. 70% of DIYers who buy a mixer use it less than 3 times',
        ],
      },
      { type: 'h2', text: 'Tips for the weekend rental' },
      {
        type: 'ul',
        items: [
          'Add water first, then aggregate, then cement. Dry-loading cement first creates lumps that never mix out.',
          'Rinse between batches. Hardened concrete in the drum is the main reason owners keep rental deposits.',
          'A 130L drum yields ~65L of wet concrete per batch. Plan your delivery and mixing order around that.',
          'Hire a wheelbarrow alongside. Moving wet concrete from mixer to site in 4-wheel garden trollies is not fun.',
        ],
      },
      {
        type: 'category-link',
        slug: 'cement-mixers',
        label: 'Rent a cement mixer near you',
      },
    ],
    faqs: [
      {
        q: 'Is a 130L cement mixer big enough for a driveway?',
        a:
          'For a domestic driveway base (100mm depth of concrete over 30m²) you need ~3m³ of concrete. A 130L mixer yields ~65L per batch so that is ~46 batches: doable but long. Larger projects benefit from ready-mix delivery.',
      },
      {
        q: 'Can I mix concrete by hand instead of renting?',
        a:
          'For anything under 0.25m³ (e.g., a small fence post batch), yes, with a mixing board and shovel. For more than that, your back will hate you and the mix quality drops.',
      },
    ],
  },
  {
    slug: 'chainsaw-safety-basics-uk',
    title: 'Chainsaw Safety Basics for UK Homeowners',
    summary:
      'Essential safety rules for using a hired chainsaw on your own land. PPE, kickback avoidance, and the things every UK homeowner gets wrong on their first cut.',
    publishedAt: '2026-04-23',
    updatedAt: '2026-04-25',
    readingTimeMinutes: 5,
    heroKeyword: 'chainsaw safety basics',
    metaTitle: 'Chainsaw Safety Basics for UK Homeowners: Essential Rules',
    metaDescription:
      'Before you start a hired chainsaw: PPE, kickback, chain tension, stance and the four rules that prevent 95% of home chainsaw injuries. UK domestic guide.',
    relatedCategories: ['chainsaws', 'hedge-trimmers', 'strimmers', 'garden-shredders'],
    relatedGuides: [],
    sections: [
      { type: 'h2', text: 'PPE: not optional' },
      {
        type: 'ul',
        items: [
          'Chainsaw trousers (cut-resistant Class 1). £50–£80 to buy and they will save your leg.',
          'Safety boots with steel toe and cut-resistant upper. Wellies with added cut protection also work for lighter work.',
          'Helmet with mesh visor and ear defenders. A bike helmet does not count.',
          'Gloves with cut protection on the left hand (the hand nearest the bar tip).',
        ],
      },
      { type: 'h2', text: 'The four rules' },
      {
        type: 'ol',
        items: [
          'Two hands, every cut. Left hand on the front handle (with thumb wrapped, not over the top), right hand on the trigger. Letting go with the left hand is how the saw kicks back into your face.',
          'Stand to the side of the cut line, not behind it. Kickback sends the bar toward the operator, so standing aside means it swings past you instead of into you.',
          'Keep the chain sharp. A blunt chain causes kickback because you have to force the cut. Feel like the saw is fighting you? Stop and sharpen (or swap chains if you rented with spares).',
          'Never cut above shoulder height without a professional setup. Home chainsaw injuries cluster around two things: cutting above the head, and branches under tension.',
        ],
      },
      { type: 'h2', text: 'Branches under tension' },
      {
        type: 'p',
        text:
          'The single most dangerous cut is a fallen branch pinned by the tree. Releasing it suddenly whips the branch, and a lot of broken teeth and broken arms come from this. Cut partway on the compression side first (the side being squeezed), then finish from the tension side with the bar ready to pull clear.',
      },
      { type: 'h2', text: 'Cordless vs petrol for home use' },
      {
        type: 'p',
        text:
          'Cordless chainsaws (18–36V) are quieter, lighter and enough for most home tasks: dead branches under 15cm, tidy-up felling, storm clearance. Petrol wins on sustained work, larger timber and when you need to work away from power. For the "one tree I need to take down this weekend" job, a cordless hire is almost always the right call.',
      },
      {
        type: 'category-link',
        slug: 'chainsaws',
        label: 'See chainsaws available to rent near you',
      },
    ],
    faqs: [
      {
        q: 'Do I need a chainsaw licence in the UK?',
        a:
          'For personal use on your own land: no. For any commercial use, paid work, or felling on land you do not own, you need NPTC CS30/31 certificates. Most professional tree surgeons also hold CS38 (aerial tree work).',
      },
      {
        q: 'What is the most common chainsaw injury for homeowners?',
        a:
          'Lacerations to the left thigh from kickback when the bar tip touches wood unexpectedly. Chainsaw trousers prevent exactly this: the fabric clogs the chain on contact.',
      },
      {
        q: 'Should I rent a petrol or cordless chainsaw?',
        a:
          'For less than 2 hours of domestic work, cordless. For an afternoon\'s storm clearance or large timber, petrol. Most UK homeowners never need more than a 36V cordless.',
      },
    ],
  },
  {
    slug: 'drilled-into-pipe-or-cable',
    title: 'Drilled Into a Pipe or Cable? The First 60 Seconds Matter',
    summary:
      'Step-by-step on what to do when a drill bit hits copper, plastic pipe, or a live cable. What you can fix yourself, what needs a tradesman, and the £40 detector that stops it happening twice.',
    publishedAt: '2026-06-07',
    updatedAt: '2026-06-07',
    readingTimeMinutes: 6,
    heroKeyword: 'drilled into pipe or cable',
    metaTitle: 'Drilled Into a Pipe or Cable? UK First-Aid Guide',
    metaDescription:
      'What to do in the first 60 seconds after drilling into a water pipe, gas pipe, or live cable. Isolate, assess, repair vs call out, plus the £40 detector that prevents a recurrence.',
    relatedCategories: ['stud-detectors', 'wet-dry-vacuums', 'dehumidifiers'],
    relatedGuides: ['cable-pipe-detection-before-drilling', 'after-a-leak-drying-guide'],
    sections: [
      {
        type: 'p',
        text:
          'Most UK home wiring uses safe zones (vertical / horizontal corridors above sockets and switches). Most plumbing does not. The combination is why "I imagined the cable would be there" is the wrong rule, and why almost every DIY drilling incident ends with a hand on a stopcock instead.',
      },
      { type: 'h2', text: 'If you have hit a water pipe' },
      {
        type: 'ol',
        items: [
          'Stop drilling. Do not pull the bit out yet: it is actually sealing the hole while it is in place.',
          'Turn off the mains stopcock (usually under the kitchen sink or in the downstairs WC). Open the lowest cold tap in the house (usually the kitchen) to drop the pressure.',
          'Open hot taps too. Drain the system above the drill point so pulling the bit does not flood.',
          'Now withdraw the bit slowly. Have a wet & dry vac or thick towel ready underneath.',
          'Photograph the hole and the pipe run before you do anything else. Useful for insurance and for the plumber.',
          'Decide: copper pinhole patch (DIY-possible with a self-amalgamating tape kit for short-term, soldered patch for long-term) or replacement section (plumber).',
        ],
      },
      {
        type: 'p',
        text:
          'A self-amalgamating repair tape kit costs around £8 and buys you 7-30 days of dry while a plumber gets booked. It is not a permanent fix and any building insurer will see it as such.',
      },
      { type: 'h2', text: 'If you have hit a gas pipe' },
      {
        type: 'p',
        text:
          'Stop. Do not continue. Turn off the gas at the meter (the lever on the inlet pipe, quarter-turn so it sits across the pipe). Ventilate the room. Phone the National Gas Emergency Service on 0800 111 999. This is non-negotiable. There is no homeowner-DIY route for a punctured gas pipe.',
      },
      { type: 'h2', text: 'If you have hit a live cable' },
      {
        type: 'p',
        text:
          'You may not have. A modern consumer unit (any installed after 2008) has RCDs that will trip the instant a current flows through the drill bit to earth. That is by design and that is what saved you. The fact that the lights went out is a feature, not a fault.',
      },
      {
        type: 'ol',
        items: [
          'Leave the bit in place. Move yourself and anyone else away.',
          'At the consumer unit, identify which RCD or breaker tripped. Do not reset it yet.',
          'If it is a single-circuit MCB you can isolate by hand, switch it off. If it is the main RCD, switch off the whole consumer unit.',
          'Now withdraw the bit. Cap the hole with electrical tape so nothing falls into the cavity.',
          'A nicked or part-severed cable is an electrician job (Part P-notifiable work in many cases). Do not just sleeve it and reset the breaker.',
        ],
      },
      { type: 'h2', text: 'What this costs to put right' },
      {
        type: 'ul',
        items: [
          'Plumber to replace ~30cm of copper pipe and re-seat: £80–£180 callout',
          'Electrician to splice and certify a part-severed cable in a chased run: £120–£250',
          'Re-plastering and decorating the affected wall section: £80–£200',
          'Buildings insurance excess (typical UK policy): £150–£300',
        ],
      },
      {
        type: 'quote',
        text:
          'Most "I drilled through a pipe" stories cost the householder a £600 weekend. A £40 detector and 30 seconds of scanning prevents almost all of them.',
      },
      {
        type: 'category-link',
        slug: 'stud-detectors',
        label: 'Rent a stud / cable / pipe detector near you',
      },
    ],
    faqs: [
      {
        q: 'Will my home insurance cover drilling into a pipe?',
        a:
          'Most UK buildings insurance covers escape of water and accidental damage, but accidental damage is usually optional, not standard. Check your policy schedule before you assume. The pipe damage itself is rarely covered; it is the consequence (wet plaster, soaked carpet) that triggers the claim.',
      },
      {
        q: 'Can I just plug the hole and ignore it?',
        a:
          'No. A nicked copper pipe behind plaster can weep for months before it shows as a damp patch. By that point the joists and plasterboard are also compromised. A self-amalgamating tape patch is a stopgap, not a fix.',
      },
      {
        q: 'Do I have to tell a buyer if I drilled through a pipe?',
        a:
          'The TA6 property information form asks about works carried out. A properly-repaired pipe is not a defect and does not need disclosure. An unrepaired or DIY-bodged one does, and non-disclosure is misrepresentation.',
      },
      {
        q: 'How do I tell if I hit a plastic pipe vs copper?',
        a:
          'Plastic pipes (push-fit barrier pipe, common in newer UK builds) are usually slate-grey or beige. Copper looks like copper. Plastic pipe damage usually means replacement of the section, since patching plastic is not reliable.',
      },
    ],
  },
  {
    slug: 'stripped-or-snapped-screw-rescue',
    title: 'Stripped or Snapped Screw? Rescue Methods That Actually Work',
    summary:
      'Six proven ways to extract a stripped, rusted, or snapped-off screw without destroying the host material. Pick the right method by situation: rubber band, extractor bit, left-hand drill, plug-cutter.',
    publishedAt: '2026-06-07',
    updatedAt: '2026-06-07',
    readingTimeMinutes: 5,
    heroKeyword: 'stripped or snapped screw rescue',
    metaTitle: 'Stripped or Snapped Screw? UK DIY Rescue Methods',
    metaDescription:
      'Step-by-step screw rescue methods for stripped Phillips heads, snapped-flush screws, and rusted-in fixings. UK techniques with tool recommendations and when an impact driver beats a regular drill.',
    relatedCategories: ['impact-drivers', 'cordless-drills', 'sds-drills'],
    relatedGuides: ['drilled-into-pipe-or-cable'],
    sections: [
      { type: 'h2', text: 'Identify the problem first' },
      {
        type: 'ul',
        items: [
          'Stripped head: the bit spins in the recess. Most common, easiest to rescue.',
          'Snapped flush with the surface: recess is gone but threads are intact.',
          'Snapped below the surface: head is gone and a stump remains in the host material.',
          'Rusted or seized: head intact but the screw will not turn.',
        ],
      },
      { type: 'h2', text: 'Stripped head: the rubber band trick' },
      {
        type: 'p',
        text:
          'Place a rubber band (a wide elastic band works best) flat over the screw head, press the driver bit firmly through it into the recess, and turn slowly. The rubber fills the gaps in the chewed-up recess and gives the bit enough grip to back the screw out. Cost: free. Works on about 60% of stripped heads.',
      },
      { type: 'h2', text: 'Stripped head, no luck with the band: screw extractor bits' },
      {
        type: 'p',
        text:
          'A screw extractor set (around £12–£25 for a 5-piece) has reverse-cutting bits. You drill a small pilot into the screw head, then switch to reverse and the extractor bit grips and backs the screw out. Always use a low-speed drill setting and back the trigger off the instant it grips. High RPM snaps the extractor inside the screw, which is a worse problem.',
      },
      { type: 'h2', text: 'Snapped flush: drill out, retap, replace' },
      {
        type: 'p',
        text:
          'If the screw is flush with the host but threads are intact in the host:',
      },
      {
        type: 'ol',
        items: [
          'Centre-punch the snapped screw face. This stops the drill bit wandering.',
          'Drill straight down with a left-handed drill bit (same diameter as the screw core). Often the reverse rotation backs the screw out before you finish drilling.',
          'If it does not back out, drill all the way through, clean the threads with a tap of the original size, and replace with a new screw.',
        ],
      },
      { type: 'h2', text: 'Snapped below the surface: plug and replace' },
      {
        type: 'p',
        text:
          'When the screw has snapped below the host surface (common in old timber where the head has rusted off), drill the snapped stump out with a plug cutter, fill the hole with a glued dowel matching the host, sand flush, and re-drill a pilot for a fresh screw. Takes 20 minutes per fixing but leaves an invisible repair. The plug cutter (£8–£15) is the only specific tool you need.',
      },
      { type: 'h2', text: 'Rusted or seized: heat and penetrating oil' },
      {
        type: 'p',
        text:
          'Spray with a proper penetrating oil (Plus Gas, not WD-40, which is a water-displacer, not a penetrant). Leave 10 minutes. Apply heat with a small butane torch for 20 seconds (avoid in flammable cavities). The differential expansion breaks the rust bond. Tap the head firmly with a hammer to send shock through the threads, then attempt to back it out.',
      },
      { type: 'h2', text: 'Prevention beats rescue: use an impact driver' },
      {
        type: 'p',
        text:
          'Most stripped screws are caused by a regular drill-driver wallowing in the recess on tight fixings. An impact driver (distinct from a hammer drill) delivers rotational hammer blows that drive the bit deep into the screw recess every impact, so it never cams out. For deck-screws, ledger boards or anything bigger than a 5mm woodscrew, an impact driver is the right tool. Day rental £8–£15 on DIY Toolshare.',
      },
      {
        type: 'category-link',
        slug: 'impact-drivers',
        label: 'Rent an impact driver near you',
      },
    ],
    faqs: [
      {
        q: 'Why does my drill keep stripping screw heads?',
        a:
          'Usually one of three things: wrong-sized bit for the screw recess (a PH2 in a PZ2 screw, or vice versa), too high a torque setting on a drill-driver, or cam-out from a worn bit. Match the bit to the head type exactly, use a clutch setting under 8 for woodscrews, and replace bits when the tip rounds off.',
      },
      {
        q: 'Are screw extractor bits reusable?',
        a:
          'A good set takes 20–40 extractions before the bit teeth wear. Cheap eBay sets often snap on the second use, which is worse than starting with no extractor: you then have hardened steel inside the screw inside the host.',
      },
      {
        q: 'Will WD-40 free a rusted screw?',
        a:
          'WD-40 is a water-displacer. It will lubricate the bit-to-head interface but does not actually penetrate corrosion. Use a proper penetrating oil (Plus Gas, PB Blaster, or similar) and give it 10 minutes to work.',
      },
    ],
  },
  {
    slug: 'plaster-blown-patches-damp',
    title: 'Plaster Gone Wrong: Blown Patches, Damp Spots, How to Fix',
    summary:
      'Diagnose hollow-tap blown patches, slow-drying damp areas, and crazing cracks before you re-skim. What is salvageable with a £15 PVA-and-fill, what needs the wall taken back to brick, and how to tell the difference.',
    publishedAt: '2026-06-07',
    updatedAt: '2026-06-07',
    readingTimeMinutes: 6,
    heroKeyword: 'plaster blown patches',
    metaTitle: 'Plaster Gone Wrong: Diagnose & Fix UK Blown Patches',
    metaDescription:
      'How to tell salvageable plaster from re-skim. Hollow-tap test, drying conditions, the right PVA-and-bonding rescue, and when a blown patch hides a leak or damp problem you need to fix first.',
    relatedCategories: ['dehumidifiers', 'wet-dry-vacuums', 'sanders'],
    relatedGuides: ['after-a-leak-drying-guide'],
    sections: [
      { type: 'h2', text: 'Diagnose before you fix' },
      {
        type: 'p',
        text:
          'A "blown" patch is plaster that has separated from the backing (usually a board or brick) while staying superficially intact on the face. Tap the suspect area with the back of a knuckle or a coin. Solid plaster sounds dull and dead. Blown plaster rings hollow.',
      },
      {
        type: 'ul',
        items: [
          'Hollow tap, no cracks visible: separated from the backing, still hanging on. Fixable by re-bonding.',
          'Hollow tap with hairline cracking: partially failed. Often fixable, sometimes needs cutting back.',
          'Hollow tap with stain or visible damp: there is a moisture source you must find first. Repairing the plaster without fixing the cause buys you 8–12 weeks before it blows again.',
          'Crumbling at the edges, sandy texture: backing failure. Cut back to sound plaster and re-skim that area.',
        ],
      },
      { type: 'h2', text: 'Find the cause if there is damp' },
      {
        type: 'p',
        text:
          'Plaster blows for one of four reasons: a leak (above, behind, below), condensation (cold-bridge wall, no extraction in bathroom/kitchen), rising damp (failed DPC), or background suction (new plaster applied to dry brick without proper PVA prep). The first three need fixing the cause before patching. The fourth is a workmanship issue and can be re-skimmed cleanly.',
      },
      { type: 'h2', text: 'Re-bond a small blown patch (under ~20cm across)' },
      {
        type: 'p',
        text:
          'If the patch is small, the face is intact, and there is no underlying moisture issue:',
      },
      {
        type: 'ol',
        items: [
          'Drill 3–5mm pilot holes into the centre of the blown area on a 50mm grid, just deep enough to reach the cavity behind the face plaster.',
          'Inject a structural plaster bonding adhesive (Carlite Bond-It or similar, about £12 a cartridge) into each hole until it backs out.',
          'Press the face firmly back to the wall, weight or prop if needed, and leave 24 hours.',
          'Fill the pilot holes with finishing plaster or fine filler, sand flush, redecorate.',
        ],
      },
      { type: 'h2', text: 'Cut back and patch (medium areas)' },
      {
        type: 'p',
        text:
          'For anything bigger than 20cm or showing edge cracks, cut the blown area out with a Stanley knife and a small bolster. Take it back to sound plaster. Wet the backing brick or board, apply diluted PVA (1:4), let it go tacky, then patch with bonding coat to within 2mm of the surface, leave to set, finish with multi-finish skim.',
      },
      { type: 'h2', text: 'Drying conditions matter' },
      {
        type: 'p',
        text:
          'Fresh patches need to dry slowly and evenly. Force-drying with a fan heater on a wet patch causes cracking. A dehumidifier in the room with the door closed and gentle airflow drops humidity without shocking the patch. Allow 3–5 days for a small patch before painting, longer if the room is cold or the original backing was damp.',
      },
      { type: 'h2', text: 'The mistakes that cost a re-do' },
      {
        type: 'ul',
        items: [
          'Skipping PVA: patches fall off backing within months',
          'Painting over still-wet plaster: paint blooms, peels, or shows ghost lines',
          'Filling a blown area with cheap powder filler: moves under temperature changes, cracks again',
          'Not addressing the leak: the wall finds the same weak spot every time',
        ],
      },
      {
        type: 'category-link',
        slug: 'dehumidifiers',
        label: 'Rent a dehumidifier to dry a patched wall properly',
      },
    ],
    faqs: [
      {
        q: 'Can I just paint over a blown patch?',
        a:
          'No. Paint adds no structural bond. The blown patch will continue to fail and will pull the new paint film with it. Re-bond the patch or cut it back before redecorating.',
      },
      {
        q: 'How can I tell if plaster is blown or just damaged at the surface?',
        a:
          'The tap test is the most reliable: a knuckle or coin tap rings hollow over a blown area, sounds solid (dull thud) over a sound one. Move across the wall in a grid, and the boundary of the blown area becomes obvious as the sound changes.',
      },
      {
        q: 'How long until I can paint a patched area?',
        a:
          '3–5 days for small finishing-plaster patches in a warm, ventilated room. Longer if cold or damp. Mist-coat (50/50 watered emulsion) the patch first to allow the plaster to continue to breathe through the first paint film. Full colour coats after that.',
      },
      {
        q: 'Do I need to find the leak before fixing the plaster?',
        a:
          'Yes, every time. Patching plaster over an unfixed leak gives you 8–12 weeks before it returns. Worse, the moisture during that time can rot timber and grow mould inside the cavity.',
      },
    ],
  },
  {
    slug: 'drilling-pre-2000-walls-asbestos-uk',
    title: 'Drilling into Pre-2000 UK Walls: The Asbestos Check You Are Skipping',
    summary:
      'Artex ceilings, AIB partitions, soffits, vinyl floor tiles: all legal in millions of UK homes, all containing asbestos. How to identify before you drill, the £40 lab test, and the £4,000 mistake.',
    publishedAt: '2026-06-07',
    updatedAt: '2026-06-07',
    readingTimeMinutes: 7,
    heroKeyword: 'asbestos pre-2000 uk drilling',
    metaTitle: 'Asbestos in UK Homes: Drilling Safely in Pre-2000 Properties',
    metaDescription:
      'Where asbestos still hides in pre-2000 UK homes (Artex, AIB, soffits, vinyl tiles). How to identify before drilling, the £40 UKAS lab test, and what to do if you have already disturbed it.',
    relatedCategories: ['wet-dry-vacuums', 'dust-extractors'],
    relatedGuides: ['drilled-into-pipe-or-cable', 'cable-pipe-detection-before-drilling'],
    sections: [
      {
        type: 'p',
        text:
          'Asbestos was banned in UK construction in 1999. Anything built or last refurbished before 2000 may contain it. About 6 million UK homes do. The risk is not the material sitting undisturbed; the risk is what happens when you drill, sand or cut into it without knowing.',
      },
      { type: 'h2', text: 'Where it still hides in UK homes' },
      {
        type: 'ul',
        items: [
          'Artex ceilings and walls (textured coating): extremely common in homes from 1965 to mid-1990s. Pre-2000 Artex frequently contains chrysotile asbestos.',
          'Asbestos Insulating Board (AIB): partition walls, ceilings, infill panels around bath surrounds, soffits, fire doors. Looks like white plasterboard but is denser.',
          'Vinyl floor tiles and their bitumen adhesive: common in 1960s-80s downstairs WCs and kitchens. The tile or the adhesive may contain it.',
          'Cement boards on garages, sheds, soffit overhangs, eaves and downpipes: common 1950s-90s.',
          'Vermiculite loft insulation: much rarer in the UK than the US, but present.',
          'Boiler flue gaskets and pipe insulation around old central heating systems.',
        ],
      },
      { type: 'h2', text: 'The rule, briefly' },
      {
        type: 'p',
        text:
          'Under the Control of Asbestos Regulations 2012, a homeowner has a duty to manage asbestos in their property. The regulations distinguish between licensed work (HSE-notifiable, requires a licensed contractor) and non-licensed work (small amounts of low-risk material). Anything that disturbs friable AIB is licensed work. Drilling a single fixing into Artex is generally treated as non-licensed work, with conditions.',
      },
      { type: 'h2', text: 'Identify before you drill' },
      {
        type: 'p',
        text:
          'There is no reliable visual test. White ceilings, white panels and grey vinyl tiles all look identical asbestos vs not. The only credible identification is a lab sample analysed by a UKAS-accredited testing laboratory.',
      },
      {
        type: 'ul',
        items: [
          'A postal sample kit costs around £30–£50 per sample (Lucion, ARCA-listed labs)',
          'Sampling is a tiny piece, cut from an unobtrusive edge while the area is wet (use a spray bottle to suppress fibres) and double-bagged',
          'Turnaround is typically 2–5 working days',
          'If you are uncomfortable taking the sample yourself, an asbestos surveyor visit is £150–£300 for a domestic management survey',
        ],
      },
      { type: 'h2', text: 'If a test confirms asbestos and you still need to drill' },
      {
        type: 'p',
        text:
          'For one or two fixings into bonded Artex containing chrysotile, HSE guidance permits homeowner DIY with strict conditions:',
      },
      {
        type: 'ol',
        items: [
          'Wet the surface continuously with a spray bottle while drilling. Dust must not become airborne.',
          'Use a low-speed drill (250 RPM or less). High speed atomises the material.',
          'Catch every fragment in a plastic sheet or a Class M / H dust extractor with HEPA filtration. A normal wet & dry vac is not adequate: the fibres are far smaller than its filter rating.',
          'P3 respirator (FFP3 or half-face with P3 cartridges). Never a nuisance dust mask.',
          'Disposable coveralls and gloves. Double-bag all waste and seal with the lab\'s asbestos waste label.',
          'Local council hazardous waste collection (typically £20–£40). Not the household bin.',
        ],
      },
      {
        type: 'p',
        text:
          'For anything beyond a single fixing (chasing, sanding, removing panels) stop and engage a licensed asbestos contractor. The £600–£1,200 a small encapsulation or removal costs is the right answer.',
      },
      { type: 'h2', text: 'If you have already drilled and only just realised' },
      {
        type: 'ol',
        items: [
          'Stop disturbing it. Do not vacuum: a normal vacuum will spray fibres back into the room.',
          'Damp down the work area with a fine mist sprayer.',
          'Open windows. Leave the room with all occupants and pets.',
          'After 30 minutes, return wearing a P3 mask, damp-wipe surfaces with disposable cloths.',
          'Double-bag the cloths, mask filters, and any dust.',
          'Contact your local council\'s environmental services for guidance on disposal.',
          'Future drilling into the same surface needs the full procedure above (or a contractor).',
        ],
      },
      { type: 'h2', text: 'The actual risk in context' },
      {
        type: 'p',
        text:
          'A single brief exposure event (one drilled hole, mishandled) is genuinely low-risk for a one-off. Most documented mesothelioma cases are tradespeople with repeated exposure over decades. The danger is the cumulative DIY habit: 50 drilled holes across 20 years of a home, each with no precautions, in a house full of bonded asbestos. Test the material once, know what is in your home, and the rest of the DIY decisions become easier.',
      },
      {
        type: 'category-link',
        slug: 'dust-extractors',
        label: 'Rent an M / H-class dust extractor for asbestos-aware work',
      },
    ],
    faqs: [
      {
        q: 'Is all Artex asbestos?',
        a:
          'No. Artex containing chrysotile was sold up to 1984; some Artex made later still contained smaller quantities until reformulation completed by 2000. Anything applied before 2000 should be assumed positive until tested. Post-2000 Artex-style textured coatings are asbestos-free.',
      },
      {
        q: 'Can I just paint over Artex to seal it?',
        a:
          'Yes. Undisturbed and sealed Artex is low-risk. Two coats of standard emulsion or a specialist sealer coat will lock fibres into the matrix. The risk only appears when you sand or cut it.',
      },
      {
        q: 'Do I need a survey to sell my pre-2000 house?',
        a:
          'Not as the seller. There is no UK legal requirement to survey a domestic property for asbestos before sale. Buyers may commission one as part of their own due diligence. Most management surveys cost £150–£300.',
      },
      {
        q: 'Can a local council come and check for free?',
        a:
          'Generally no. Domestic asbestos management is the homeowner\'s responsibility. Some councils offer subsidised disposal of small bagged quantities, but identification and sampling is your cost. Citizens Advice can signpost local resources.',
      },
    ],
  },
  {
    slug: 'cable-pipe-detection-before-drilling',
    title: 'Cable & Pipe Detection Before Drilling: The £40 That Saves £400',
    summary:
      'Why the "safe zone" mental model fails on old UK wiring, what £15 vs £40 vs £150 actually buys in a detector, and how to scan a wall in 60 seconds before you commit to a hole.',
    publishedAt: '2026-06-07',
    updatedAt: '2026-06-07',
    readingTimeMinutes: 5,
    heroKeyword: 'cable pipe detector before drilling',
    metaTitle: 'Cable & Pipe Detection Before Drilling: UK Buyer\'s Guide',
    metaDescription:
      'Why mental wiring rules fail in pre-2000 UK homes, what tier of cable detector you actually need, and a 60-second scan routine that finds copper pipe, live cable and metal studs before the drill bit does.',
    relatedCategories: ['stud-detectors', 'thermal-imaging-cameras', 'cordless-drills'],
    relatedGuides: ['drilled-into-pipe-or-cable'],
    sections: [
      { type: 'h2', text: 'Why the mental rule fails' },
      {
        type: 'p',
        text:
          'UK Part P wiring regulations say cables in walls should run in vertical or horizontal corridors above or below a socket or switch (the "safe zone"). The rule is real and applies to new work. The problem: any UK home built before 1992, and most before 2008, has wiring that pre-dates or pre-interprets that rule. Diagonal cable runs in older homes are common. Plumbing has no such convention at all.',
      },
      { type: 'p', text: 'Treat every wall as suspect until proven otherwise.' },
      { type: 'h2', text: 'What each price tier actually buys' },
      {
        type: 'ul',
        items: [
          '£10–£20 stud detector (DIY chain own-brand): detects metal studs only. Will miss live cable behind foil insulation, will miss plastic pipe, will miss copper deeper than ~25mm. Avoid.',
          '£25–£45 multi-mode detector (Bosch GMS-class, Stanley S-series): three modes covering AC live, ferrous metal (rebar, pipes) and non-ferrous (copper, stud). 40–50mm depth, accurate within 5mm. This is the price tier most UK DIYers should own.',
          '£80–£150 pro detector (Bosch D-tect, Hilti PS-class): depth display (in cm), reliable through 80mm, sometimes plastic-pipe-with-water detection. Worth it if you drill into walls more than monthly.',
          '£300+ thermal-imaging add-on for phone: finds live wiring by heat signature, finds water leaks. Niche but useful for diagnostic work.',
        ],
      },
      { type: 'h2', text: '60-second wall scan routine' },
      {
        type: 'ol',
        items: [
          'Calibrate against a known-empty wall area, letting the detector self-zero.',
          'Sweep slowly. Most detectors fail because users move them at speed.',
          'Scan three passes: vertical (covers horizontal cables), horizontal (covers vertical cables), diagonal (covers old non-compliant runs).',
          'Mark any hit with a pencil: full square around the suspected line, not a single dot.',
          'Move 20cm and re-scan. Many detectors give false positives near metal fixings, metallic foil wallpaper, or steel mesh on lath-and-plaster walls, so confirm before trusting a positive.',
        ],
      },
      { type: 'h2', text: 'The moisture-meter trick for hidden plumbing' },
      {
        type: 'p',
        text:
          'A pinless moisture meter (around £15) reads through the wall surface. Run it slowly along the suspected pipe run. Active hot pipes raise the surface moisture reading slightly because of thermal expansion in the surrounding plaster, even where no leak is present. Cold mains pipes show up similarly in well-insulated walls. Not foolproof but a useful belt-and-braces check alongside a cable detector.',
      },
      { type: 'h2', text: 'When to rent vs buy' },
      {
        type: 'p',
        text:
          'If you drill into walls more than once a month, a £40 multi-mode detector pays for itself in one avoided plumber callout. For a one-off project (new shelves, a single TV bracket) renting from a neighbour is the right call. A weekend rental on DIY Toolshare typically runs £6–£12.',
      },
      {
        type: 'category-link',
        slug: 'stud-detectors',
        label: 'Rent a stud / cable / pipe detector for your next drilling project',
      },
    ],
    faqs: [
      {
        q: 'Do I really need a detector for a small picture hook?',
        a:
          'For a 25mm fixing into surface plaster you are usually safe. For anything that puts a bit deeper than 25mm (TV bracket, shelf, radiator, kitchen unit) yes. The depth where you start hitting cables and copper buried in plaster is 30–40mm.',
      },
      {
        q: 'Why does my detector beep on empty walls?',
        a:
          'Common causes: lath-and-plaster walls with steel laths beneath, metallic foil-backed plasterboard, mineral wool insulation with binder mesh, magnetic radiator brackets behind the wall. Calibrate against a clearly empty section and recalibrate as you move around.',
      },
      {
        q: 'Can detectors find plastic pipes?',
        a:
          'Empty plastic pipe: no. Water-filled plastic pipe: only the £150+ pro detectors with capacitive sensing. The 60-second scan routine plus the moisture meter trick is the workaround if your wall has push-fit barrier pipe (common in newer builds).',
      },
    ],
  },
  {
    slug: 'mdf-dust-respirator-selection',
    title: 'MDF Dust Isn\'t Wood Dust: Why Your Mask Doesn\'t Cut It',
    summary:
      'MDF cutting and sanding releases formaldehyde and ultrafine particles a nuisance dust mask passes straight through. Why FFP3 matters, when to step up to a half-face respirator, and the £30 you should not skip.',
    publishedAt: '2026-06-07',
    updatedAt: '2026-06-07',
    readingTimeMinutes: 5,
    heroKeyword: 'mdf dust respirator',
    metaTitle: 'MDF Dust Respirator Guide: What Mask, When, Why',
    metaDescription:
      'Why FFP1 and FFP2 masks fail against MDF dust, what FFP3 and P3 actually filter, and when a half-face respirator beats disposables. UK respirator standards and selection by project.',
    relatedCategories: ['dust-extractors', 'sanders', 'orbital-sanders', 'jigsaws'],
    relatedGuides: ['refinish-wooden-floor-weekend'],
    sections: [
      { type: 'h2', text: 'What MDF dust actually is' },
      {
        type: 'p',
        text:
          'Medium-density fibreboard is wood fibre bonded with urea-formaldehyde resin. Cut, drilled or sanded, it releases two things: wood dust particles in the respirable range (under 5 microns, which the body cannot expel), and formaldehyde residues at concentrations that depend on the sheet age and grade. The HSE classifies wood dust (MDF included) as a workplace exposure limit substance.',
      },
      { type: 'h2', text: 'Why your standard dust mask fails' },
      {
        type: 'p',
        text:
          'UK respirator filtration is graded FFP1 / FFP2 / FFP3 (disposable) and P1 / P2 / P3 (cartridge filters).',
      },
      {
        type: 'ul',
        items: [
          'FFP1 / P1: blocks 80% of particles down to 0.6 microns. For nuisance dust only (sawdust, plaster, light cement). Not adequate for MDF.',
          'FFP2 / P2: blocks 94% of particles. Adequate for general wood dust on natural timber, marginal for MDF.',
          'FFP3 / P3: blocks 99% of particles down to 0.6 microns. This is the minimum standard for MDF.',
          'Half-face respirator with P3 cartridges: 99.95%. Substantially better fit than a disposable, all-day comfort, around £30 plus £15–£20 for replacement filters.',
        ],
      },
      { type: 'h2', text: 'Fit matters more than filter rating' },
      {
        type: 'p',
        text:
          'A perfectly-rated FFP3 with a poor seal (beard, glasses, wrong size) leaks far more air around the edge than it filters through the front. Air takes the path of least resistance. For occasional sanding, beard owners and glasses wearers should default to a half-face respirator with replaceable cartridges. The silicone seal moulds around facial hair in a way disposable masks cannot.',
      },
      { type: 'h2', text: 'When to step up to extraction' },
      {
        type: 'p',
        text:
          'A respirator protects the wearer. It does nothing for the rest of the room, the dust settled on the floor, or the person who walks in afterwards. For MDF projects beyond a single shelf cut, dust extraction at source is the difference between a usable workshop and a contaminated room.',
      },
      {
        type: 'ul',
        items: [
          'Class L extractor: general wood dust. Not adequate for MDF in commercial use; OK for one-off DIY with a respirator.',
          'Class M extractor: HSE-mandated for tradespeople working with wood and most construction materials, including MDF. 99% efficiency, audible alarm if airflow drops. £40–£70/day rental.',
          'Class H extractor: required for hazardous materials (asbestos, lead, certain biological hazards). Overkill for MDF; relevant for the asbestos guide.',
        ],
      },
      { type: 'h2', text: 'Practical setup for an MDF weekend project' },
      {
        type: 'ol',
        items: [
          'Cut outdoors if at all possible. Wind does the extraction for you.',
          'Indoor cuts: a Class M dust extractor connected directly to the saw or sander hose port, FFP3 mask, closed door to the rest of the house.',
          'Open one window for cross-flow only after the cut is finished and the extractor is still running.',
          'Damp-wipe surfaces afterwards. Sweeping airborne MDF dust on settled surfaces back into circulation defeats the point.',
          'Wash work clothes separately. MDF dust transfers to other fabrics in a normal wash.',
        ],
      },
      { type: 'h2', text: 'Cost of doing it properly' },
      {
        type: 'ul',
        items: [
          'FFP3 disposable masks: £2–£4 each, single use',
          'Half-face respirator + P3 cartridges: £30–£50 once, £15 for replacement filters every 40 hours',
          'Class M dust extractor rental: £40–£70/day',
          'Buying a Class M extractor: £200–£400 new, break-even at ~6 rental days',
        ],
      },
      {
        type: 'category-link',
        slug: 'dust-extractors',
        label: 'Rent a Class M dust extractor for safe MDF cutting',
      },
    ],
    faqs: [
      {
        q: 'Is the smell of MDF when I cut it actually formaldehyde?',
        a:
          'Partly yes, and by the time you can smell it, the airborne concentration is already approaching the HSE exposure limit. Smell is a poor warning; absence of smell is not safety. Use the right mask before you start, not when you notice.',
      },
      {
        q: 'Are E0 / E1 / CARB MDF grades safer to cut without a mask?',
        a:
          'They release less formaldehyde over time when in use, but cutting and sanding releases the same wood-dust component regardless of grade. The ultrafine particle hazard is independent of formaldehyde class. Respirator and extraction still required.',
      },
      {
        q: 'Can I just open a window and a fan?',
        a:
          'Ventilation alone moves dust around the room before it leaves. Without source extraction, MDF dust settles in carpet, furniture and ventilation ducts and re-aerosolises every time the room is disturbed. Source extraction beats ventilation.',
      },
      {
        q: 'Is MDF really that bad? People have cut it for decades.',
        a:
          'Long-term cumulative wood dust exposure is associated with sino-nasal cancer risk; HSE classifies wood dust as a workplace carcinogen. For an occasional weekend project the marginal risk is small, but the difference in cost between doing it right and doing it badly is £30. There is no upside to skipping a mask.',
      },
    ],
  },
  {
    slug: 'diy-garden-paving-uk-guide',
    title: 'Laying a Patio Yourself: UK Weekend Guide to Garden Paving',
    summary:
      'How to lay a 10–15m² patio properly (sub-base depth, mortar bed, cutting slabs cleanly, pointing) without the bowing, sinking and weed problems most DIY patios show within two years.',
    publishedAt: '2026-06-09',
    updatedAt: '2026-06-09',
    readingTimeMinutes: 8,
    heroKeyword: 'diy garden paving uk',
    metaTitle: 'DIY Garden Paving: Lay a UK Patio in a Weekend (Honest Guide)',
    metaDescription:
      'Step-by-step guide to laying a patio yourself in the UK: sub-base depth, mortar mix, cutting slabs, jointing options. 2026 tool rental costs and the mistakes that cause patios to fail in year two.',
    relatedCategories: ['cement-mixers', 'wacker-plates', 'tile-cutters', 'angle-grinders', 'wheelbarrows', 'laser-levels'],
    relatedGuides: ['cement-mixer-rent-or-buy', 'build-dream-bbq-garden-gazebo-uk'],
    sections: [
      {
        type: 'p',
        text:
          'A patio is one of the most rewarding weekend DIY projects: visible transformation, immediate use, and a fraction of the £100–£140/m² a contractor charges. It is also the project most often done badly: poor sub-base, no fall, wrong jointing, and a slab field that bows or sinks within two summers. The good news is that the right way and the wrong way take roughly the same time and tools. The difference is sub-base discipline.',
      },
      { type: 'h2', text: 'How much patio for one weekend?' },
      {
        type: 'p',
        text:
          'A realistic scope for two people across one weekend is 10–15m² (roughly a 3×4m or 3×5m area). Larger areas are absolutely DIYable but become a two-weekend job: sub-base prep + slabs over a long weekend, pointing the following weekend once the bedding mortar has cured. Push past 25m² in one weekend and corners get cut, usually in the sub-base.',
      },
      { type: 'h2', text: 'Tools you actually need' },
      {
        type: 'ul',
        items: [
          'Wacker plate (vibrating plate compactor): £35–£55/day. Non-negotiable. Hand-tamping a sub-base never compacts enough.',
          'Cement mixer (small 90L electric is plenty for a domestic patio): £20–£30/day on DIY Toolshare',
          'Wheelbarrow + builder\'s buckets: £8–£12/day or borrow',
          'Angle grinder with 4.5" diamond blade: £15–£25/day. Or a wet-cut tile/paving cutter for cleaner cuts and far less dust',
          'Long spirit level (1200mm minimum) or laser level: £8–£20/day for a laser',
          'String line, pegs, tape measure',
          'Pointing trowel, bucket trowel, lump hammer + bolster chisel',
          'Knee pads (you will thank yourself by hour three)',
          'Materials: paving slabs, MOT Type 1 sub-base aggregate, sharp sand, building sand, cement (or buy ready-mix bagged mortar to skip mixing)',
        ],
      },
      { type: 'h2', text: 'Sub-base: the part everyone rushes' },
      {
        type: 'p',
        text:
          'Failed patios almost always fail at the sub-base, not the slab layer. The slab is the thing you see; the sub-base is what carries the load. For a foot-traffic patio (no vehicles), the spec is straightforward:',
      },
      {
        type: 'ol',
        items: [
          'Mark out and excavate. Total depth from finished patio surface: 150mm (75mm MOT Type 1 sub-base + 50mm mortar bed + the slab thickness, usually around 25–30mm). Slope away from any building at 1:80 (1cm fall per 80cm of patio length) for drainage.',
          'Fill with MOT Type 1 sub-base aggregate, in two 35–40mm layers.',
          'Compact each layer with the wacker plate. Three to four full passes per layer minimum. Walking on it should not leave any footprint impression.',
          'Re-check fall and finished level with string lines or laser. The sub-base surface should be exactly the slab thickness + 50mm below your finished patio target.',
        ],
      },
      {
        type: 'p',
        text:
          'Skipping the second compacted layer is the single most common mistake. One thick lift of aggregate never compacts as well as two thinner ones: the wacker plate cannot transmit force through the full depth in a single pass.',
      },
      { type: 'h2', text: 'Bedding the slabs' },
      {
        type: 'p',
        text:
          'Use a full wet mortar bed, not the five-dot "spot bedding" you may see on YouTube. Spot bedding leaves voids under the slab that fill with water, freeze, and crack the slab from underneath within a couple of winters. A 4:1 sharp sand and cement mix, mixed to a stiff (not sloppy) consistency, laid 50mm thick under each slab. Lay one slab at a time, tap level with a rubber mallet against a spirit level, then move on. Leave 8–10mm joints between slabs.',
      },
      { type: 'h2', text: 'Cutting slabs accurately' },
      {
        type: 'p',
        text:
          'You will need to cut slabs for the perimeter and around any drains, manholes or planters. An angle grinder with a 115mm diamond blade is cheap and fast but throws a huge amount of silica dust, so wear a P3 mask, not a nuisance dust mask (see our MDF dust guide for the rating reasoning). A wet-cut tile/paving cutter rented for the day costs £25–£40 and gives a cleaner, dust-free cut with no chip-out on the visible face.',
      },
      { type: 'h2', text: 'Pointing: three options' },
      {
        type: 'ul',
        items: [
          'Mortar pointing (4:1 sharp sand / cement, slightly damp): cheapest, most weather-durable, hardest to apply cleanly. Stains the slab face if you smear.',
          'Polymeric jointing compound (Romex, EasyJoint, Geo-Fix): brush dry into joints, mist with water, sets like rubber. Easy to apply, weed-suppressing, £30–£50 per 20kg tub. Best for most domestic patios.',
          'Kiln-dried sand: cheapest option, brushed into joints dry. Fine for permeable installations or where appearance is not critical. Weeds grow through eventually.',
        ],
      },
      { type: 'h2', text: 'Falls and drainage' },
      {
        type: 'p',
        text:
          'A flat patio against a house is a liability: rainwater runs back to the wall and finds a damp course defect within 12 months. A 1:80 fall (1cm drop per 80cm of length) is invisible to the eye but moves water effectively to a gully, lawn edge or a French drain. For patios over ~20m², two falls in a shallow V toward a central drain works better than one long single-direction fall.',
      },
      { type: 'h2', text: 'Common mistakes that wreck the result' },
      {
        type: 'ul',
        items: [
          'Skimping on sub-base (single-lift aggregate, no wacker): end result sinks in tracks where you walked',
          'Spot bedding instead of full wet bed: slabs crack from voids holding water and freezing',
          'No fall away from the house: damp problems within a winter',
          'Cutting slabs dry with no mask: silica dust is a long-term lung hazard, not just irritation',
          'Pointing before bedding mortar cures (allow 48h minimum): slabs slip under their own weight',
          'Walking on the patio for 48h after pointing: pressed-out joints pop loose',
        ],
      },
      {
        type: 'category-link',
        slug: 'wacker-plates',
        label: 'Rent a wacker plate to compact your patio sub-base properly',
      },
    ],
    faqs: [
      {
        q: 'Do I need planning permission to lay a patio in the UK?',
        a:
          'Domestic patios fall under permitted development as long as the material is porous (or drains to a permeable area) and the patio is not raised more than 30cm above ground level. Front-garden patios over 5m² that drain to a road or pavement require planning permission since the 2008 SUDS regulations. Back gardens are usually fine.',
      },
      {
        q: 'What sub-base depth do I actually need?',
        a:
          '75mm of compacted MOT Type 1 is correct for foot-traffic patios on most UK soils. Clay-heavy ground or known soft spots: 100mm. Anything that will take vehicle weight (driveway, not patio): 150mm minimum + edge restraints + different bedding method.',
      },
      {
        q: 'Can I lay paving directly on grass or soil?',
        a:
          'You can, but it will move within months. Roots, frost heave and soil settlement push slabs out of level. The sub-base + mortar bed combination is what stops that. Quick "stepping stone" paths through a lawn are a different story; full patios need the proper layers.',
      },
      {
        q: 'Mortar bed vs full wet bed: which is right?',
        a:
          'Full wet mortar bed (a 50mm continuous layer of stiff 4:1 mix under each slab) for any patio that will see furniture, BBQs, foot traffic. Spot bedding (five blobs) is faster but creates voids that crack slabs in freeze-thaw cycles. Lay one slab at a time, tap level, move on.',
      },
      {
        q: 'How long before I can walk on a new patio?',
        a:
          'After bedding: 24 hours before light foot traffic, 48 hours before pointing. After pointing: 24 hours before normal use, 7 days before furniture or BBQ weight. Avoid pressure-washing or heavy scrubbing for 4 weeks while the mortar fully cures.',
      },
    ],
  },
  {
    slug: 'build-dream-bbq-garden-gazebo-uk',
    title: 'Build Your Dream BBQ Garden: Gazebo, Patio, Pizza Oven (UK Guide)',
    summary:
      'Designing and building an outdoor entertaining space across a series of weekends: gazebo kit-vs-custom, foundations, lighting, BBQ choices. Real UK 2026 budgets and the calls worth paying a pro for.',
    publishedAt: '2026-06-09',
    updatedAt: '2026-06-09',
    readingTimeMinutes: 9,
    heroKeyword: 'build bbq garden gazebo uk',
    metaTitle: 'Dream BBQ Garden: Gazebo, Patio & Outdoor Build (UK DIY Guide)',
    metaDescription:
      'Plan and build an outdoor entertaining space yourself. Gazebo kit vs custom timber, patio sub-base, lighting and power, pizza-oven decisions. Real 2026 UK budgets and the tools you will need.',
    relatedCategories: ['cement-mixers', 'sds-drills', 'mitre-saws', 'circular-saws', 'paint-sprayers', 'impact-drivers'],
    relatedGuides: ['diy-garden-paving-uk-guide', 'cement-mixer-rent-or-buy'],
    sections: [
      {
        type: 'p',
        text:
          'A "BBQ garden" (a dedicated outdoor space with cover overhead, a proper patio underfoot, lighting for the evenings and somewhere to actually cook) is one of the highest-return DIY projects you can do. Adds £8–£15k to a typical UK house value, transforms summer use of the garden, and an experienced DIYer can deliver the whole thing for £2,500–£5,000 in materials over a series of weekends. The same finished result built by a landscaper runs £12,000–£20,000.',
      },
      {
        type: 'p',
        text:
          'The trick is sequencing: getting the order right so you do not have to undo earlier work. This guide is the planning frame and the major decision points, not a step-by-step bill of materials.',
      },
      { type: 'h2', text: 'Plan in zones, not features' },
      {
        type: 'p',
        text:
          'The mistake everyone makes is starting from "I want a pizza oven" or "I want a gazebo". Start from how the space will actually be used and what zones it needs.',
      },
      {
        type: 'ul',
        items: [
          'Prep zone: counter space, sink (optional), storage for tools and consumables',
          'Cooking zone: BBQ, pizza oven or smoker. Wind direction matters: smoke should not blow onto seated guests',
          'Eating zone: table for 6–8 minimum if entertaining. Allow 1m of chair pull-out clearance on all sides',
          'Lounge zone: softer seating, optional fire pit or chiminea, evening focal point',
        ],
      },
      {
        type: 'p',
        text:
          'A 4×6m footprint can comfortably contain all four zones with the gazebo covering the prep + cooking sides only (the eating zone benefits from open sky and stars on a good evening). Smaller spaces work fine: drop the lounge zone or merge the eating + lounge zones.',
      },
      { type: 'h2', text: 'The gazebo decision: kit vs custom build' },
      {
        type: 'p',
        text:
          'A pre-fabricated timber gazebo kit (3×3m or 3×4m) costs £600–£1,400 in 2026, arrives flat-packed, and a competent DIY pair can erect it over a long weekend. The roof shingles, paint or stain, and concrete post anchors add another £200–£400.',
      },
      {
        type: 'p',
        text:
          'A custom timber-framed build using treated softwood costs £400–£900 in materials for the same footprint but takes 3–4 weekends and requires accurate framing carpentry. Worth it if you want custom dimensions (e.g. fitting an awkward corner), specific timber species (oak frame looks superb but doubles the cost), or an asymmetric pitched roof.',
      },
      {
        type: 'p',
        text:
          'For the first BBQ garden project, the kit is almost always the right call. Custom builds make sense once you have the patio, electrics and use patterns established and you know exactly what you want.',
      },
      { type: 'h2', text: 'Foundations: get this right or everything else moves' },
      {
        type: 'p',
        text:
          'A gazebo on top of a patio works only if the patio sub-base was built for it. Three approaches in order of cost:',
      },
      {
        type: 'ul',
        items: [
          'Slab pad with post anchors (cheapest, fastest): patio slabs across the whole footprint, gazebo post anchors bolted through into a sub-base concrete pad below each post. Good for kits up to ~3×4m.',
          'Concrete pad foundations under each post: 300×300×400mm concrete cubes set below frost depth (450mm in most of UK), with post anchors cast into the concrete. Strongest, slightly more disruptive to install.',
          'Post-in-ground (Postsaver): direct-bury treated posts wrapped in Postsaver tape. Cheapest if you are skipping a patio. Fails first at the ground line in 8–12 years.',
        ],
      },
      {
        type: 'p',
        text:
          'If you are also laying the patio (see our paving guide), build the sub-base across the WHOLE area first (including under the gazebo footprint) and cast the gazebo post anchors into a thickened sub-base section before laying any slabs. Doing this in the wrong order means lifting slabs to fit anchors later.',
      },
      { type: 'h2', text: 'Tools you will need over a multi-weekend build' },
      {
        type: 'ul',
        items: [
          'Wacker plate for the patio sub-base: £35–£55/day',
          'Cement mixer for foundation concrete + bedding mortar: £20–£30/day',
          'SDS drill (rotary hammer) for masonry fixings and anchor bolts: £15–£25/day',
          'Mitre saw for accurate timber framing cuts on roof framing and trims: £15–£25/day',
          'Circular saw for cross-cutting structural timber on site: £10–£18/day',
          'Impact driver for deck-screws and structural fixings (a regular drill driver will cam-out)',
          'Spirit levels, string lines, chalk line, square',
          'Paint sprayer if you are staining a large gazebo: £20–£30/day, much faster than a brush',
        ],
      },
      { type: 'h2', text: 'The build sequence that does not unwind itself' },
      {
        type: 'ol',
        items: [
          'Mark out the full footprint with pegs and string, including all four zones and where the gazebo posts will land. Live with the layout for at least one evening. Drag a chair in and sit there.',
          'Excavate the patio sub-base AND the deeper post-anchor holes in the same dig. Cheaper to do all the earthwork once than to come back and dig pockets later.',
          'Cast the gazebo post-anchor concrete pads. Set anchors plumb. Allow 7 days to cure (concrete reaches ~70% strength in 7 days).',
          'Compact the patio sub-base around the cured anchor pads.',
          'Run any underground services NOW: armoured cable for electrics, mains water if a sink is going in, any drainage. Once the patio is laid, this becomes a major job.',
          'Lay the patio slabs over the sub-base, falling away from the house and the gazebo zone to a drain or planted edge.',
          'Erect the gazebo frame onto the post anchors. Allow patio bedding mortar 48h to cure before bolting the frame down: vibration can lift slabs.',
          'Roof the gazebo, fit roof trims, paint or stain.',
          'Fit electrics: light fittings, sockets, RCBO at the consumer unit end. This is Part P-notifiable work in England and Wales. Either do the rough-in yourself and have a registered electrician test and certify, or use a registered electrician for the whole electrical install.',
          'Move in furniture, BBQ, plants and start using it.',
        ],
      },
      { type: 'h2', text: 'Lighting and power: what is DIY and what is not' },
      {
        type: 'p',
        text:
          'Running 240V mains from the house to the gazebo is notifiable work under Part P (England and Wales). You can dig the trench, lay armoured SWA cable in conduit, install the gazebo-end socket box and the light fittings (all the labour-intensive parts), but a registered electrician must test, certify and notify the work. Expect £180–£350 for the certifier visit and notification.',
      },
      {
        type: 'p',
        text:
          'Low-voltage 12V lighting (festoon strings, plug-in LED spots, solar lanterns) is not notifiable and is genuinely the easier first-iteration choice. Run a single weatherproof outdoor socket from the house to power a 12V transformer in the gazebo, then run as many 12V circuits as you like off that. Plenty for ambience; only worth upgrading to 240V if you need a fridge, sound system or proper task lighting for cooking after dark.',
      },
      { type: 'h2', text: 'BBQ, pizza oven, fire pit: built-in vs freestanding' },
      {
        type: 'p',
        text:
          'Built-in masonry BBQs and brick pizza ovens look stunning in design magazines and are immobile, fire-rated, and a long commitment. They also crack if the substrate moves and cost £800–£2,500 in materials. Freestanding options (Weber kettle BBQ, Ooni-style portable pizza oven, steel chiminea) are £150–£600, can be put away over winter to preserve them, and let you change your mind in 18 months without demolition.',
      },
      {
        type: 'p',
        text:
          'For a first build, lean freestanding. If after two seasons you still want a built-in, then commit. The freestanding kit also doubles up: wheel the pizza oven to a friend\'s house for their BBQ.',
      },
      { type: 'h2', text: 'Planning, insurance and neighbour notes' },
      {
        type: 'ul',
        items: [
          'A gazebo under 30m² floor area and under 2.5m eaves height is permitted development in most UK back gardens, so no planning permission is needed. Front gardens and listed buildings always need a check.',
          'Building Regulations apply if the structure is over 30m² or has electrics, gas or a habitable use. A simple shaded gazebo with low-voltage festoons stays clear of Building Regs.',
          'Tell your home insurer once it is built. Adds £15–£40/year to most policies. Failing to declare the structure can void garden-content claims.',
          'Neighbours: a tall gazebo on a boundary line can block their light. A quick chat before you build avoids a permitted-development objection ruining your weekend.',
        ],
      },
      {
        type: 'category-link',
        slug: 'mitre-saws',
        label: 'Rent a mitre saw for accurate gazebo framing cuts',
      },
    ],
    faqs: [
      {
        q: 'Do I need planning permission for a garden gazebo in the UK?',
        a:
          'Most domestic garden gazebos are permitted development if under 30m² floor area, under 2.5m at the eaves (3m for a dual-pitched roof), more than 2m from the boundary if over 2.5m total height, and located in a back garden. Front gardens, conservation areas and listed buildings need a planning check before you build.',
      },
      {
        q: 'Should I buy a kit gazebo or build from scratch?',
        a:
          'For a first BBQ garden, kit almost always wins. £600–£1,400 vs £400–£900 in materials, but the kit goes up in a weekend with two people, versus 3–4 weekends for accurate timber framing. Custom builds make sense when you need awkward dimensions or want a specific timber species.',
      },
      {
        q: 'Can I run mains power to the gazebo myself?',
        a:
          'You can do the labour (trench, armoured cable, sockets, fittings) but a Part P-registered electrician must test, certify and notify the installation in England and Wales. Budget £180–£350 for the certifier. Low-voltage 12V lighting off a single weatherproof outdoor socket is not notifiable and is easier for a first build.',
      },
      {
        q: 'How long does building a BBQ garden actually take?',
        a:
          'A typical 4×6m space with patio, kit gazebo, festoon lighting and a freestanding BBQ: 4–6 weekends including drying time between concrete pours and paint coats. Add 2–3 weekends if running 240V mains or building a brick pizza oven. Done across April–July, you get half a season of use the same year.',
      },
      {
        q: 'What is the cheapest part to splurge on?',
        a:
          'Patio slab quality. Cheap concrete slabs delaminate and stain within five years; porcelain or proper natural stone lasts decades and looks better every year. A 10–15m² patio that costs £40/m² in good slabs vs £15/m² in budget ones is £375 extra, and is the surface you will see and stand on every time you use the space.',
      },
    ],
  },
];

const BY_SLUG = Object.fromEntries(GUIDES.map(g => [g.slug, g]));

export const getGuideBySlug = slug => BY_SLUG[slug] || null;
export const getAllGuideSlugs = () => GUIDES.map(g => g.slug);
